import { useState, useEffect } from "react";

export const useSpeechRecognition = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

    useEffect(() => {
        if (!SpeechRecognition) {
            setError("Speech Recognition API is not supported in this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            const result = event.results[0][0].transcript;
            setTranscript(result);
        };

        recognition.onerror = (event) => {
            setError(`Error: ${event.error}`);
            setIsListening(false); // Stop listening on error
        };

        // Using onend event with type assertion to avoid TypeScript errors
        (recognition as any).onend = () => {
            setIsListening(false); // Reset listening state when speech ends
        };

        if (isListening) {
            recognition.start();
        } else {
            recognition.stop(); // Stop recognition if isListening is false
        }

        // Clean up and abort recognition when component unmounts
        return () => {
            recognition.abort();
        };
    }, [isListening]);

    const startListening = () => {
        if (!isListening) {
            setError(null); // Clear any previous errors
            setIsListening(true);
        }
    };

    const stopListening = () => {
        if (isListening) {
            setIsListening(false); // Stop the recognition process
        }
    };

    return {
        isListening,
        transcript,
        error,
        startListening,
        stopListening,
    };
};
